import {
  CreateProductRequest,
  GetProductsRequest,
  GetProductsResponse,
  Product,
  UpdateProductRequest,
} from '@/types/ProductType';
import { rtkApi } from '../rtkApi';

export const productApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query<GetProductsResponse, GetProductsRequest>({
      query: (query) => ({ url: `/product`, params: query }),
      providesTags: ['Product'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs?.transactionMode ?? 'all'}-${
          queryArgs?.contractStarted ?? ''
        }`;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (currentCache?.products?.length && arg.offset) {
          return {
            products: [...currentCache.products, ...newResponse.products],
            isEnd: newResponse.isEnd,
          };
        }
        return newResponse;
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.offset !== previousArg?.offset ||
          currentArg?.contractStarted !== previousArg?.contractStarted ||
          currentArg?.transactionMode !== previousArg?.transactionMode
        );
      },
    }),
    getProduct: builder.query<Product, string>({
      query: (productId) => ({ url: `/product/${productId}` }),
      providesTags: (_response, _result, productId) => [
        { type: 'Product', id: productId },
      ],
    }),
    createProduct: builder.mutation<Product, CreateProductRequest>({
      query: (payload) => ({
        url: `/product`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['Product'],
    }),
    updateProduct: builder.mutation<void, UpdateProductRequest>({
      query: (payload) => ({
        url: `/product/${payload.id}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (_response, _result, payload) => [
        { type: 'Product', id: payload.id },
      ],
    }),
    uploadMortgageStatement: builder.mutation<
      { success: boolean; product: Product },
      { productId: string; file: File }
    >({
      query: ({ productId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/product/${productId}/refinance/mortgage-statement`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: (_response, _result, { productId }) => [
        { type: 'Product', id: productId },
        'Product',
      ],
    }),
    startContract: builder.mutation<void, string>({
      query: (productId) => ({
        url: `/product/${productId}/contract`,
        method: 'POST',
      }),
      invalidatesTags: (_response, _result, productId) => [
        { type: 'Product', id: productId },
        { type: 'Product' },
      ],
    }),
    removeProduct: builder.mutation<void, string>({
      query: (productId) => ({
        url: `/product/${productId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(productId, { queryFulfilled, dispatch, getState }) {
        try {
          await queryFulfilled;
          const cache = productApi.util.selectInvalidatedBy(getState(), [
            { type: 'Product' },
          ]);
          cache
            .filter(({ endpointName }) => endpointName === 'getProducts')
            .forEach(({ originalArgs }) => {
              dispatch(
                productApi.util.updateQueryData(
                  'getProducts',
                  originalArgs,
                  (data) => {
                    data.products = data.products.filter(
                      (product: Product) => product._id !== productId
                    );
                    return data;
                  }
                )
              );
            });
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useUploadMortgageStatementMutation,
  useStartContractMutation,
  useRemoveProductMutation,
} = productApi;
